import { useCallback, useMemo } from "react";
import useFixaNavigate from "@coworker/apprestructured/src/shared/wrappers/FixaNavigate/useFixaNavigate";
import useFixaLocation from "@coworker/apprestructured/src/shared/wrappers/FixaLocation/useFixaLocation";

// These are the search params this hook is able to handle
const searchParamKeys = [
  // Show the modal for creating a new task.
  // Value is the task type id of the task to create.
  "taskCreator",

  // Show the modal for viewing a task.
  // Value is the id of the task to view.
  "taskViewer",

  // Show the modal for editing a task.
  // Value is the id of the task to edit.
  "taskEditor",

  // Show the modal for creating a new task with pre-filled values.
  // Value is a JSON stringified object with the initial values for the task.
  "initialValues",

  "taskDetails",
  "tab",

  // Show the modal for viewing a testbuy.
  // Value is the id of the testbuy to view.
  "testbuyId",
  "testbuyArticleId",

  // Generic "return to" mechanism: any sheet that replaces its own params with
  // taskCreator/taskViewer (rather than stacking on top) can set this to the params it
  // wants restored once taskCreator/taskViewer closes again. Value is a URI-encoded query
  // string, e.g. encodeURIComponent("testbuyId=1&testbuyArticleId=2"). Generic and not
  // aware of any particular sheet's params - see restoreReturnTo()/encodeReturnTo().
  "returnTo",
] as const;

type SearchParamStateKey = (typeof searchParamKeys)[number];

// Typed wrapper for URLSearchParams, only allows SearchParamStateKey keys
class TypedSearchParams {
  private params: URLSearchParams;
  constructor(params: URLSearchParams) {
    this.params = params;
  }
  set(key: SearchParamStateKey, value: string) {
    this.params.set(key, value);
  }
  delete(key: SearchParamStateKey) {
    this.params.delete(key);
  }
  toString() {
    return this.params.toString();
  }

  /**
   * If a "returnTo" param is present, applies the params it encodes and removes
   * "returnTo" itself. No-op if there's nothing to restore. Generic: doesn't know or care
   * which sheet set it, only that it decodes to whitelisted SearchParamStateKeys.
   */
  restoreReturnTo() {
    const returnTo = this.params.get("returnTo");
    if (!returnTo) return;

    const restoredParams = new URLSearchParams(decodeURIComponent(returnTo));
    restoredParams.forEach((value, key) => {
      if ((searchParamKeys as readonly string[]).includes(key)) {
        this.params.set(key, value);
      }
    });
    this.params.delete("returnTo");
  }
}

/**
 * Builds the value for a "returnTo" param: a URI-encoded query string of the given params,
 * to be restored later via restoreReturnTo(). Skips nullish values.
 */
export function encodeReturnTo(
  params: Partial<Record<SearchParamStateKey, string | null | undefined>>
): string {
  const returnToParams = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value) returnToParams.set(key, value);
  }
  return encodeURIComponent(returnToParams.toString());
}

type SearchParamStateValues = { [K in SearchParamStateKey]: string | null };

/**
 * Holds state in the url search params.
 *
 * Example: /some/path?createTask=1
 *
 * useSearchParamState()
 * returns
 * [ searchParams, setSearchParams ]
 * where
 * searchParams
 * is
 * { taskCreator: 1, taskViewer: null }
 *
 * Calling
 * setSearchParamsState((prev) => {
 *   prev.delete("taskCreator");
 *   prev.set("taskViewer", 2);
 * })
 * navigates to
 * /some/path?taskViewer=2
 */
function useSearchParamState(): [
  SearchParamStateValues,
  (updater: (params: TypedSearchParams) => void) => void,
] {
  const navigate = useFixaNavigate();
  const { pathname, search } = useFixaLocation();

  const params = useMemo<SearchParamStateValues>(() => {
    const urlParams = new URLSearchParams(search);
    return Object.fromEntries(
      searchParamKeys.map((key) => [key, urlParams.get(key)])
    ) as SearchParamStateValues;
  }, [search]);

  const setParams = useCallback(
    (updater: (prev: TypedSearchParams) => void) => {
      const urlParams = new TypedSearchParams(new URLSearchParams(search));
      updater(urlParams);
      const paramString = urlParams.toString();
      navigate(`${pathname}${paramString ? `?${paramString}` : ""}`);
    },
    [navigate, pathname, search]
  );

  return [params, setParams];
}

export default useSearchParamState;
